import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Users, ScrollText, Shield, Car, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LiveStats from "@/components/LiveStats";
import EconomyStats from "@/components/EconomyStats";
import { api } from "@/lib/api";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const SERVER_IP = "cfx.re/join/r77aj7";
const SERVER_CONNECT_URL = "https://cfx.re/join/r77aj7";
const DISCORD_URL = "https://discord.gg/mjc3uWVbGj";

const RULES = [
  {
    id: "general",
    title: "General Conduct",
    body: "Treat every player with respect out of character. No racism, hate speech, harassment, discrimination, or targeted OOC toxicity — one warning, then a ban. Do not advertise other servers, leak player information, or impersonate staff. Staff decisions are final in the moment; disputes go through a Discord ticket, never in-game arguments. English only in public voice chat.",
  },
  {
    id: "metagaming",
    title: "Metagaming & External Comms",
    body: "Never use information your character did not learn in-game. Discord calls, streams, DMs, and group chats are off-limits for in-character knowledge — if your character didn't hear it, they don't know it. Streaming the server is allowed, but using another player's stream to gain an advantage (stream-sniping) is an instant ban. Third-party voice (Discord calls) may not be used as in-game radio; all comms stay in-city.",
  },
  {
    id: "powergaming",
    title: "Powergaming & FailRP",
    body: "No forcing actions on others without giving them a chance to respond — use /me and /do fairly and accept outcomes. No superhuman feats, ignoring injuries, or abusing game mechanics (emote abuse to escape cuffs, combat-logging, using /carry to dodge RP). Stay in character at all times; if you must go AFK, find a quiet spot and use /me afk. Cop baiting with no story purpose is FailRP.",
  },
  {
    id: "fearrp",
    title: "FearRP & Value of Life",
    body: "Your character fears death and values their life. If a weapon is aimed at you and you have no reasonable escape, comply with demands. Don't sprint into active gunfire, don't pull a weapon on multiple armed attackers, and don't act invincible. When injured, roleplay your injuries realistically — a gunshot to the chest is not a shrug.",
  },
  {
    id: "nlr",
    title: "New Life Rule (NLR)",
    body: "When your character dies, they forget everything leading up to and including their death. Do not return to the area of your death for 15 minutes, do not re-join the same situation, and do not seek revenge — your previous life's enemies are strangers now. Dying to server bugs or scum rule-breaks can be appealed via ticket for a possible revive.",
  },
  {
    id: "rdm",
    title: "RDM, VDM & Combat Initiation",
    body: "Random Deathmatch (killing without story or initiation) and Vehicle Deathmatch (using a car as a weapon without cause) are bannable offenses. All hostile action requires clear verbal initiation and a valid in-character reason — 'hands up or you die' counts, silent drive-bys on strangers don't. Ongoing gang wars with established beef do not need re-initiation between known rivals, but randoms are never fair game.",
  },
  {
    id: "robberies",
    title: "Robberies & Heists",
    body: "Store robberies: max 4 participants, at least 2 police on duty, 30-minute server-wide cooldown between hits by the same crew. Banks and the Pacific Standard: max 6 participants, at least 4 police on duty, staff approval via ticket required 24h ahead. You must bring a believable plan — hostages, getaway, and negotiation are expected, not optional. No robbing the same location twice in one restart cycle, and no heists within the first or last 30 minutes of a restart.",
  },
  {
    id: "hostages",
    title: "Hostages & Kidnapping",
    body: "Hostages must be willing participants or roleplayed with genuine value — taking friends as fake hostages for a payout is FailRP and punished for everyone involved. You may not kidnap players in green zones or during active emergency scenes. Demands must be reasonable: max $25,000 per civilian hostage, and police hostages carry heavier consequences in-character. Executing a hostage after demands are met is powergaming unless the story justifies it.",
  },
  {
    id: "gangs",
    title: "Gangs & Territory",
    body: "Gangs cap at 8 members and must be registered with staff via ticket, including colors, turf, and leadership. Turf wars require buildup — spray tagging, warnings, and story before the shooting starts. No robbing or attacking brand-new players (under 10 hours in city), no camping spawn points, clothing stores, or job centers. Alliance pacts between gangs need staff approval so wars stay balanced.",
  },
  {
    id: "crime-civ",
    title: "Civilian Crime & Mugging",
    body: "Muggings require initiation and a story reason; you may take carried cash and one valuable item, never someone's entire inventory, phone, or ID. No robbing players at ATMs, banks, job payout points, or while they're in menus — that's abusing mechanics. Chains of robberies back-to-back are not allowed: after any successful mugging, lay low for 20 minutes before your next one.",
  },
  {
    id: "exploits",
    title: "Exploits, Cheats & Bugs",
    body: "Any mod menus, injected clients, aimbots, ESP, or macro tools mean a permanent ban with no appeal. Found a dupe, money glitch, or bug? Report it via ticket — using it is treated the same as cheating, and reporting it earns protection and possibly a reward. Do not use crosshairs, third-party overlays, or altered game files that give an advantage.",
  },
  {
    id: "emergency",
    title: "Police & EMS Interaction",
    body: "Value your freedom: fleeing every minor traffic stop into a 45-minute shootout is low-quality RP. In custody, roleplay processing — no spamming, no escaping through clearly locked mechanics. Do not interfere with active emergency scenes as a bystander. Corruption RP for whitelisted police/EMS requires prior staff approval. Fake 911 calls to bait or ambush responders require initiation and a story.",
  },
];

const FEATURES = [
  {
    icon: ScrollText,
    title: "Serious Story-Driven RP",
    text: "Deep character arcs, player-run businesses, and an economy that rewards creativity over grinding.",
  },
  {
    icon: Car,
    title: "Custom Cars & Scripts",
    text: "Hand-tuned vehicles, custom MLOs, jobs, and illegal activities you won't find anywhere else.",
  },
  {
    icon: Shield,
    title: "Active Whitelist Staff",
    text: "Every application is reviewed by real staff. Fair rule enforcement keeps the city immersive 24/7.",
  },
];

const FAQS = [
  {
    q: "How do I join Southside Roleplay?",
    a: "Sign in with Google, submit a whitelist application on our Apply page, and once staff approve it, connect in your FiveM client via cfx.re/join/r77aj7. Most applications are reviewed within 48 hours.",
  },
  {
    q: "Is Southside Roleplay a whitelisted FiveM server?",
    a: "Yes. Every player passes a written application covering their character backstory and real RP scenarios. Whitelisting keeps trolls, cheaters and low-effort players out of the city.",
  },
  {
    q: "What makes Southside different from other GTA 5 RP servers?",
    a: "Serious story-first roleplay in the style players love from big communities like NoPixel — custom cars and scripts, a real player-driven economy with live public stats, and active staff who actually enforce the rulebook.",
  },
  {
    q: "Is it free to play, and what do I need?",
    a: "Completely free. You need a legal PC copy of Grand Theft Auto V and the free FiveM mod installed. No paid entry, ever — Southside is never pay-to-win.",
  },
  {
    q: "How many players are online right now?",
    a: "Check the live stats panel on our homepage — it shows real-time player count, peak players today and server status straight from the server, updating every 10 seconds.",
  },
];

export default function Landing() {
  const [copied, setCopied] = useState(false);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    api.get("/members").then((res) => setMembers(res.data.members || [])).catch(() => {});
  }, []);

  const copyIp = async () => {
    try {
      await navigator.clipboard.writeText(SERVER_IP);
      setCopied(true);
      toast.success("Server address copied — see you in the city");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy address");
    }
  };

  return (
    <div className="min-h-screen bg-plum-900" data-testid="landing-page">
      <div className="scanlines" />
      <Navbar />

      {/* HERO */}
      <section id="hero" className="relative overflow-hidden pt-20">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/trailer-poster.jpg"
          data-testid="hero-background-video"
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source src="/trailer.mp4" type="video/mp4" />
          <source src="/trailer.webm" type="video/webm" />
        </video>
        <div className="absolute inset-0 bg-plum-900/60" />
        <div className="absolute inset-0 retro-grid" />
        <div className="absolute inset-0 bg-gradient-to-b from-plum-900/40 via-plum-900/70 to-plum-900" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-32 flex flex-col items-start">
          <img
            src="/logo.png"
            alt="Southside Roleplay sunset logo"
            data-testid="hero-logo"
            className="h-28 w-28 sm:h-36 sm:w-36 rounded-2xl border border-sunset-amber/40 shadow-[0_0_50px_rgba(247,148,30,0.35)] animate-fade-up"
          />
          <h1
            data-testid="hero-title"
            className="mt-8 font-display font-extrabold uppercase leading-[0.9] text-5xl sm:text-6xl lg:text-8xl tracking-tight animate-fade-up"
            style={{ animationDelay: "120ms" }}
          >
            <span className="sunset-text">Southside</span>
            <br />
            <span className="text-cream">Roleplay</span>
          </h1>
          <p
            className="mt-6 max-w-xl text-base sm:text-lg text-muted-foreground animate-fade-up"
            style={{ animationDelay: "240ms" }}
          >
            A whitelisted FiveM community where the sun never sets on a good story.
            Build your character, run the streets, and write your own legend in the city.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4 animate-fade-up" style={{ animationDelay: "360ms" }}>
            <Link
              to="/apply"
              data-testid="hero-apply-cta"
              className="btn-sunset rounded-full px-8 py-3 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2"
            >
              Apply for Whitelist <ChevronRight className="h-4 w-4" />
            </Link>
            <a
              href={SERVER_CONNECT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-connect-cta"
              className="btn-outline-amber rounded-full px-8 py-3 text-sm font-semibold uppercase tracking-wider"
            >
              Join Server
            </a>
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-discord-cta"
              className="btn-outline-amber rounded-full px-8 py-3 text-sm font-semibold uppercase tracking-wider"
            >
              Join Discord
            </a>
            <button
              data-testid="hero-server-ip-button"
              onClick={copyIp}
              className="flex items-center gap-2 rounded-full border border-sunset-amber/30 bg-plum-800/70 px-5 py-3 font-mono text-sm text-sunset-gold hover:border-sunset-amber/70 transition-colors"
            >
              {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
              {SERVER_IP}
            </button>
          </div>
        </div>
      </section>

      {/* LIVE STATS */}
      <section id="server-stats" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <LiveStats />
      </section>

      {/* ECONOMY */}
      <section id="economy" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-wide text-cream mb-8">
          City <span className="sunset-text">Economy</span>
        </h2>
        <EconomyStats />
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-wide text-cream mb-8">
          Why <span className="sunset-text">Southside</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              data-testid={`feature-card-${i}`}
              className="glow-card rounded-2xl border border-sunset-amber/20 bg-plum-700/70 p-6 sm:p-8"
            >
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-sunset-amber to-sunset-orange flex items-center justify-center mb-5">
                <f.icon className="h-5 w-5 text-plum-900" />
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-semibold uppercase tracking-wide text-cream mb-2">{f.title}</h3>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* RULES */}
      <section id="rules" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-wide text-cream mb-2">
          City <span className="sunset-text">Rulebook</span>
        </h2>
        <p className="text-muted-foreground mb-8 text-sm sm:text-base">
          Know these before you apply. Ignorance is not a defence in Southside.
        </p>
        <Accordion type="single" collapsible className="space-y-3" data-testid="rules-accordion">
          {RULES.map((r) => (
            <AccordionItem
              key={r.id}
              value={r.id}
              data-testid={`rules-accordion-item-${r.id}`}
              className="rounded-xl border border-sunset-amber/20 bg-plum-700/70 px-5 overflow-hidden"
            >
              <AccordionTrigger
                data-testid={`rules-tab-${r.id}`}
                className="font-display text-lg font-semibold uppercase tracking-wide text-cream hover:text-sunset-amber hover:no-underline"
              >
                {r.title}
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                {r.body}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* MEMBERS */}
      <section id="members" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="flex items-center gap-3 mb-8">
          <Users className="h-6 w-6 text-sunset-amber" />
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-wide text-cream">
            Whitelisted <span className="sunset-text">Citizens</span>
          </h2>
        </div>
        {members.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-sunset-amber/30 bg-plum-700/40 p-10 text-center" data-testid="members-empty">
            <p className="text-muted-foreground">No citizens whitelisted yet. Your story could be the first one told.</p>
            <Link to="/apply" data-testid="members-apply-cta" className="btn-sunset rounded-full px-6 py-2.5 text-sm font-bold uppercase tracking-wider inline-block mt-4">
              Apply Now
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="members-grid">
            {members.map((m) => (
              <div
                key={m.user_id}
                data-testid={`member-card-${m.user_id}`}
                className="glow-card rounded-2xl border border-sunset-amber/20 bg-plum-700/70 p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Avatar className="h-12 w-12 border border-sunset-amber/40">
                    <AvatarImage src={m.picture} alt={m.name} />
                    <AvatarFallback className="bg-plum-600 text-cream">{m.name?.[0] || "C"}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="font-semibold text-cream truncate">{m.name}</p>
                    <p className="text-xs text-sunset-amber font-mono truncate" data-testid={`member-discord-${m.user_id}`}>{m.discord_name}</p>
                  </div>
                </div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Character</p>
                <p className="font-display text-lg font-semibold text-sunset-gold truncate">{m.character_name}</p>
                {m.ingame && (
                  <div className="mt-3 rounded-lg bg-plum-800/70 border border-emerald-400/20 px-3 py-2" data-testid={`member-ingame-${m.user_id}`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse-dot" />
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-400">Live from server</span>
                    </div>
                    <p className="text-xs text-cream/80 truncate" data-testid={`member-ingame-job-${m.user_id}`}>
                      {m.ingame.job}{m.ingame.gang ? ` · ${m.ingame.gang}` : ""}
                    </p>
                    <p className="text-xs font-mono text-sunset-gold" data-testid={`member-ingame-worth-${m.user_id}`}>
                      ${(m.ingame.cash + m.ingame.bank).toLocaleString()} net worth
                    </p>
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  Citizen since {new Date(m.joined_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* FAQ — SEO */}
      <section id="faq" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-wide text-cream mb-2">
          GTA RP <span className="sunset-text">Questions, Answered</span>
        </h2>
        <p className="text-muted-foreground mb-8 text-sm sm:text-base max-w-2xl">
          Southside Roleplay is a whitelisted GTA 5 roleplay server on FiveM — serious RP, custom
          scripts and a living economy. Everything new players ask before joining a GTA RP community:
        </p>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              data-testid={`faq-item-${i}`}
              className="rounded-xl border border-sunset-amber/20 bg-plum-700/70 px-5 overflow-hidden"
            >
              <AccordionTrigger
                data-testid={`faq-question-${i}`}
                className="font-display text-lg font-semibold tracking-wide text-cream hover:text-sunset-amber hover:no-underline text-left"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* APPLY CTA */}
      <section id="apply" className="relative overflow-hidden">
        <div className="absolute inset-0 retro-grid opacity-60" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-cream">
            Ready to run the <span className="sunset-text">Southside</span>?
          </h2>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto text-sm sm:text-base">
            Submit your whitelist application, tell us your character's story, and our staff team
            will review it within 48 hours.
          </p>
          <Link
            to="/apply"
            data-testid="cta-apply-button"
            className="btn-sunset rounded-full px-10 py-4 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 mt-8"
          >
            Start Your Application <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
